import { Platform } from 'react-native';
import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getKioskDeviceConfig } from '../config/kioskConfig';
import type {
  AttendanceCapture,
  FaceIdentifyResult,
  FacePunchResult,
  SelectableAttendanceAction,
} from '../types/faceAttendance';
import {
  breakStateFromEvents,
  parseDurationToMinutes,
} from '../utils/checkoutBreak';
import { FaceFlowError, safeFaceFlowError } from '../utils/faceAttendanceErrors';
import { toKioskFaceError } from '../utils/kioskErrors';
import { browserAttendanceImage } from '../utils/browserAttendanceImage';
import { deleteTemporaryImage } from '../utils/temporaryImage';

type Envelope<T> = { success: boolean; message: string; data: T };
type RecordValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function firstNumber(row: RecordValue, keys: string[]): number | null {
  for (const key of keys) {
    const value = numberOrNull(row[key]);
    if (value !== null) return value;
  }
  return null;
}

function durationMinutesOrNull(value: unknown): number | null {
  const numeric = numberOrNull(value);
  if (numeric !== null && numeric >= 0) return Math.round(numeric);
  if (typeof value !== 'string' || !value.trim()) return null;
  const clock = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (clock) {
    return Number(clock[1]) * 60 + Number(clock[2]);
  }
  return parseDurationToMinutes(value);
}

function firstDurationMinutes(sources: RecordValue[], keys: string[]): number | null {
  let zero: number | null = null;
  for (const row of sources) {
    for (const key of keys) {
      const minutes = durationMinutesOrNull(row[key]);
      if (minutes === null) continue;
      if (minutes > 0) return minutes;
      zero = 0;
    }
  }
  return zero;
}

function eventsFrom(row: RecordValue): Array<{
  eventType: string | null;
  localTimestamp: string | null;
  timestamp: string | null;
}> {
  const raw = Array.isArray(row.events) ? row.events : [];
  return raw.map(item => {
    const event = (item ?? {}) as RecordValue;
    return {
      eventType: textOrNull(event.eventType),
      localTimestamp: textOrNull(
        event.eventLocalTimestamp ?? event.localTimestamp,
      ),
      timestamp: textOrNull(event.eventTimestampUtc ?? event.eventTimestamp),
    };
  });
}

function isSelectableAttendanceAction(
  value: unknown,
): value is SelectableAttendanceAction {
  return value === 'BREAK_OUT' || value === 'BREAK_IN' || value === 'CHECK_OUT';
}

function punchEndpoint(action: SelectableAttendanceAction): string {
  if (action === 'BREAK_OUT') return API_ENDPOINTS.ATTENDANCE.BREAK_OUT;
  if (action === 'BREAK_IN') return API_ENDPOINTS.ATTENDANCE.BREAK_IN;
  return API_ENDPOINTS.ATTENDANCE.CHECK_OUT;
}

/** Prefer backend minute fields used elsewhere; fall back to hours or punch span. */
function extractWorkedMinutes(
  data: RecordValue,
  attendance: RecordValue,
): number | null {
  const sources = [data, attendance];
  const minuteKeys = [
    'netWorkMinutes',
    'workedMinutes',
    'netWorkedMinutes',
    'grossWorkMinutes',
    'totalWorkedMinutes',
  ];
  for (const row of sources) {
    const minutes = firstNumber(row, minuteKeys);
    if (minutes !== null && minutes >= 0) return Math.round(minutes);
  }

  const hourKeys = ['workedHours', 'totalWorkedHours', 'netWorkHours'];
  for (const row of sources) {
    const hours = firstNumber(row, hourKeys);
    if (hours !== null && hours >= 0) return Math.round(hours * 60);
  }

  for (const row of sources) {
    const checkIn = textOrNull(
      row.firstCheckInAt ??
        row.firstCheckInLocal ??
        row.checkInTime ??
        row.checkInAt,
    );
    const checkOut = textOrNull(
      row.lastCheckOutAt ??
        row.lastCheckOutLocal ??
        row.checkOutTime ??
        row.checkOutAt,
    );
    if (!checkIn || !checkOut) continue;
    const from = new Date(checkIn.includes(' ') ? checkIn.replace(' ', 'T') : checkIn);
    const to = new Date(checkOut.includes(' ') ? checkOut.replace(' ', 'T') : checkOut);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) continue;
    const minutes = Math.round((to.getTime() - from.getTime()) / 60000);
    if (minutes >= 0) return minutes;
  }

  return null;
}

function extractBreakMinutes(
  data: RecordValue,
  attendance: RecordValue,
): number | null {
  const persisted = firstDurationMinutes([data, attendance], [
    'breakMinutes',
    'lunchBreakMinutes',
    'totalBreakMinutes',
  ]);
  const fromEvents =
    breakStateFromEvents([
      ...eventsFrom(data),
      ...eventsFrom(attendance),
    ]).completedBreakMinutes;
  if (persisted != null && persisted > 0) return persisted;
  return fromEvents ?? persisted;
}

async function appendImage(form: FormData, image: AttendanceCapture) {
  if (Platform.OS === 'web') {
    const captured = await browserAttendanceImage(image);
    captured.appendTo(form);
    return;
  }
  form.append('image', {
    uri: image.uri,
    name: image.fileName,
    type: image.mimeType,
  } as unknown as Blob);
}

function normalizeEmployee(row: RecordValue) {
  const employeeId = numberOrNull(row.employeeId);
  if (!employeeId) return null;
  return {
    employeeId,
    employeeUuid: textOrNull(row.employeeUuid),
    employeeNumber: textOrNull(row.employeeNumber),
    displayName: textOrNull(row.displayName),
  };
}

export async function identifyEmployeeFace(input: {
  image: AttendanceCapture;
  captureSource: string;
  kioskId?: number | null;
}): Promise<FaceIdentifyResult> {
  const form = new FormData();
  await appendImage(form, input.image);
  form.append('captureSource', input.captureSource);
  if (input.kioskId) form.append('kioskId', String(input.kioskId));

  try {
    const response = await apiClient.post<Envelope<RecordValue>>(
      API_ENDPOINTS.FACE.IDENTIFY,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    const data = response.data.data ?? {};
    if (data.identified === false) {
      throw new FaceFlowError('FACE_NOT_FOUND');
    }
    const employee = normalizeEmployee((data.employee as RecordValue) || {});
    const registration = (data.registration as RecordValue) || {};
    const identification = (data.identification as RecordValue) || {};
    const provider = (data.provider as RecordValue) || {};
    const faceRegistrationId = numberOrNull(registration.faceRegistrationId);
    if (!employee || !faceRegistrationId) {
      throw new FaceFlowError('FACE_NOT_FOUND');
    }

    return {
      identified: true,
      employee,
      registration: {
        faceRegistrationId,
        biometricProviderId: numberOrNull(registration.biometricProviderId),
        biometricProvider: textOrNull(registration.biometricProvider),
      },
      identification: {
        confidence: numberOrNull(identification.confidence),
        qualityForRecognition: textOrNull(identification.qualityForRecognition),
      },
      provider: {
        providerCode: textOrNull(provider.providerCode),
        biometricProviderId: numberOrNull(provider.biometricProviderId),
        similarity: numberOrNull(provider.similarity ?? identification.confidence),
      },
    };
  } catch (error) {
    throw safeFaceFlowError(error);
  }
}

/**
 * POST /attendance/face-punch for office kiosk.
 * Sends session kioskId/deviceIdentifier for compatibility; backend binds kiosk from Kiosk JWT.
 * Never sends expectedEmployeeId — identity is face-only; backend chooses action.
 */
export async function submitFacePunch(input: {
  image: AttendanceCapture;
  captureSource: 'KIOSK';
  kioskId: number;
  deviceIdentifier: string;
  /** Phase 2 only — omit for Phase 1 kiosk. */
  livenessSessionId?: number | null;
}): Promise<FacePunchResult> {
  const form = new FormData();
  await appendImage(form, input.image);
  form.append('captureSource', input.captureSource);
  form.append('kioskId', String(input.kioskId));
  form.append('deviceIdentifier', input.deviceIdentifier.trim().slice(0, 255));
  if (input.livenessSessionId) {
    form.append('livenessSessionId', String(input.livenessSessionId));
  }

  try {
    const response = await apiClient.post<Envelope<RecordValue>>(
      API_ENDPOINTS.ATTENDANCE.FACE_PUNCH,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    const data = response.data.data ?? {};
    const actionRaw = textOrNull(data.action);
    const action =
      actionRaw === 'CHECK_IN' ||
      actionRaw === 'CHECK_OUT' ||
      actionRaw === 'BREAK_OUT' ||
      actionRaw === 'BREAK_IN' ||
      actionRaw === 'ACTION_REQUIRED'
        ? actionRaw
        : null;
    const employee = normalizeEmployee((data.employee as RecordValue) || {});
    const registration = (data.registration as RecordValue) || {};
    const identification = (data.identification as RecordValue) || {};
    const attendance = (data.attendance as RecordValue) || {};
    const faceVerificationLogId = numberOrNull(data.faceVerificationLogId);
    const eventTime = textOrNull(data.eventTime);
    const attendanceMode =
      textOrNull(data.attendanceMode) ||
      textOrNull(attendance.attendanceMode) ||
      textOrNull(attendance.attendance_mode);
    const workedMinutes = extractWorkedMinutes(data, attendance);
    const breakMinutes = extractBreakMinutes(data, attendance);

    if (!action || !employee || !faceVerificationLogId || !eventTime) {
      throw new FaceFlowError('FACE_PUNCH_FAILED');
    }

    return {
      action,
      eventTime,
      workedMinutes,
      breakMinutes,
      employee,
      registration: {
        faceRegistrationId: numberOrNull(registration.faceRegistrationId),
        biometricProviderId: numberOrNull(registration.biometricProviderId),
        biometricProvider: textOrNull(registration.biometricProvider),
      },
      faceVerificationLogId,
      attendanceMode,
      livenessSessionId: numberOrNull(data.livenessSessionId),
      identification: {
        confidence: numberOrNull(identification.confidence),
        providerCode: textOrNull(identification.providerCode),
      },
      availableActions: Array.isArray(data.availableActions)
        ? data.availableActions.filter(isSelectableAttendanceAction)
        : [],
    };
  } catch (error) {
    throw safeFaceFlowError(error);
  }
}

export async function submitVerifiedAttendanceAction(input: {
  action: SelectableAttendanceAction;
  employeeId: number;
  faceVerificationLogId: number;
  attendanceMode: string;
  kioskId: number;
  deviceIdentifier: string;
  livenessSessionId?: number | null;
  breakStartTime?: string | null;
  breakEndTime?: string | null;
}): Promise<{
  action: SelectableAttendanceAction;
  eventTime: string;
  workedMinutes: number | null;
  breakMinutes: number | null;
}> {
  const eventTime = new Date().toISOString();
  const endpoint = punchEndpoint(input.action);
  const response = await apiClient.post<Envelope<RecordValue>>(endpoint, {
    employeeId: input.employeeId,
    eventTimestamp: eventTime,
    attendanceSource: input.attendanceMode,
    kioskId: input.kioskId,
    deviceIdentifier: input.deviceIdentifier,
    faceVerificationLogId: input.faceVerificationLogId,
    ...(input.livenessSessionId
      ? { livenessSessionId: input.livenessSessionId }
      : {}),
    ...(input.breakStartTime?.trim() && input.breakEndTime?.trim()
      ? {
          breakStartTime: input.breakStartTime.trim(),
          breakEndTime: input.breakEndTime.trim(),
        }
      : {}),
  });
  const attendance = response.data.data ?? {};
  return {
    action: input.action,
    eventTime,
    workedMinutes: extractWorkedMinutes(attendance, attendance),
    breakMinutes: extractBreakMinutes(attendance, attendance),
  };
}

/**
 * Walk-up kiosk punch: already-captured face → face-punch → backend action.
 * No employee login. No expectedEmployeeId. No liveness (Phase 2).
 * `retainImage` keeps the capture for the caller (the Mark Attendance visitor
 * flow reuses it), which must then free it with `releaseCapturedImage`.
 */
export async function runKioskFacePunch(
  image: AttendanceCapture,
  options: { retainImage?: boolean } = {},
): Promise<FacePunchResult> {
  try {
    const kiosk = getKioskDeviceConfig();
    return await submitFacePunch({
      image,
      captureSource: kiosk.captureSource,
      kioskId: kiosk.kioskId,
      deviceIdentifier: kiosk.deviceIdentifier,
    });
  } catch (error) {
    throw toKioskFaceError(error);
  } finally {
    if (image?.uri && !options.retainImage) {
      if (Platform.OS === 'web' && image.uri.startsWith('blob:')) {
        URL.revokeObjectURL(image.uri);
      } else {
        await deleteTemporaryImage(image.uri).catch(() => undefined);
      }
    }
  }
}
